import { path } from 'playcanvas';

class DroppedFile {
    filename: string;
    file: File;
    handle?: FileSystemFileHandle;
    // the dropped item this file came from: the file itself or an enclosing folder
    root?: FileSystemHandle;

    constructor(filename: string, file: File, handle?: FileSystemFileHandle, root?: FileSystemHandle) {
        this.filename = filename;
        this.file = file;
        this.handle = handle;
        this.root = root;
    }
}

type DropHandlerFunc = (files: Array<DroppedFile>, resetScene: boolean) => void;

const resolveDirectories = (entries: Array<FileSystemEntry>): Promise<Array<FileSystemFileEntry>> => {
    const promises: Promise<Array<FileSystemFileEntry>>[] = [];
    const result: Array<FileSystemFileEntry> = [];

    entries.forEach((entry) => {
        if (entry.name === '.DS_Store') {
            return;
        }

        if (entry.isFile) {
            result.push(entry as FileSystemFileEntry);
        } else if (entry.isDirectory) {
            promises.push(
                new Promise<any>((resolve, reject) => {
                    const reader = (entry as FileSystemDirectoryEntry).createReader();

                    const p: Promise<any>[] = [];

                    const read = () => {
                        reader.readEntries((children: Array<FileSystemEntry>) => {
                            if (children.length > 0) {
                                p.push(resolveDirectories(children));
                                read();
                            } else {
                                Promise.all(p).then((children: Array<Array<FileSystemFileEntry>>) => {
                                    resolve(children.flat());
                                });
                            }
                        });
                    };
                    read();
                })
            );
        }
    });

    return Promise.all(promises).then((children: Array<Array<FileSystemFileEntry>>) => {
        return result.concat(...children);
    });
};

// collect the files under a dropped handle, named by their path relative to the
// drop (so a dropped folder 'foo' yields 'foo/a.ply', matching resolveDirectories)
const resolveHandles = async (handle: FileSystemHandle, prefix: string, result: Array<DroppedFile>, root: FileSystemHandle) => {
    if (handle.name === '.DS_Store') {
        return;
    }

    if (handle.kind === 'file') {
        const fileHandle = handle as FileSystemFileHandle;
        result.push(new DroppedFile(prefix + handle.name, await fileHandle.getFile(), fileHandle, root));
    } else {
        for await (const child of (handle as FileSystemDirectoryHandle).values()) {
            await resolveHandles(child, `${prefix}${handle.name}${path.delimiter}`, result, root);
        }
    }
};

const removeCommonPrefix = (urls: Array<DroppedFile>) => {
    const split = (pathname: string) => {
        const parts = pathname.split(path.delimiter);
        const base = parts[0];
        const rest = parts.slice(1).join(path.delimiter);
        return [base, rest];
    };
    while (true) {
        const parts = split(urls[0].filename);
        if (parts[1].length === 0) {
            return;
        }
        for (let i = 1; i < urls.length; ++i) {
            const other = split(urls[i].filename);
            if (parts[0] !== other[0]) {
                return;
            }
        }
        for (let i = 0; i < urls.length; ++i) {
            urls[i].filename = split(urls[i].filename)[1];
        }
    }
};

// expand picked or dropped handles into files, descending into folders
const resolveHandleFiles = async (handles: FileSystemHandle[]) => {
    const files: Array<DroppedFile> = [];
    for (const handle of handles) {
        await resolveHandles(handle, '', files, handle);
    }

    if (files.length > 1) {
        removeCommonPrefix(files);
    }

    return files;
};

// configure drag and drop
const CreateDropHandler = (target: HTMLElement, dropHandler: DropHandlerFunc) => {

    const dragstart = (ev: DragEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        ev.dataTransfer.effectAllowed = 'all';
    };

    const dragover = (ev: DragEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        ev.dataTransfer.effectAllowed = 'all';
    };

    const drop = async (ev: DragEvent) => {
        ev.preventDefault();

        const items = Array.from(ev.dataTransfer.items);

        // Prefer file system handles where the browser provides them (Chromium):
        // a document keeps its handle for later saves, and every imported file
        // keeps one so a save can recognise a file the scene still reads from.
        // The handles must be requested before the handler first yields, after
        // which the items are no longer readable.
        if (items.every(item => item.getAsFileSystemHandle)) {
            const handles = await Promise.all(items.map(item => item.getAsFileSystemHandle()));
            dropHandler(await resolveHandleFiles(handles.filter(handle => handle)), ev.shiftKey);
            return;
        }

        // Map to entries first
        const entries = items
        .map(item => item.webkitGetAsEntry())
        .filter(v => v);

        // resolve directories to files
        const resolvedEntries = await resolveDirectories(entries);

        const files = await Promise.all(
            resolvedEntries.map((entry) => {
                return new Promise<DroppedFile>((resolve, reject) => {
                    entry.file((entryFile: any) => {
                        resolve(new DroppedFile(entry.fullPath.substring(1), entryFile));
                    });
                });
            })
        );

        if (files.length > 1) {
            // if all files share a common filename prefix, remove it
            removeCommonPrefix(files);
        }

        // finally, call the drop handler
        dropHandler(files, ev.shiftKey);
    };

    target.addEventListener('dragstart', dragstart, true);
    target.addEventListener('dragover', dragover, true);
    target.addEventListener('drop', drop, true);
};

export { CreateDropHandler, resolveHandleFiles };
